// IdentityGate — top-level orchestrator for the Layer 0 identity wizard.
//
// Renders as a full-screen modal that blocks the coding test until
// verification reaches an acceptable verdict ('passed' or 'skipped').
//
// Step machine:
//   loading            → spinner while we hydrate config + status
//   skipped            → all sub-features off, immediately resolve onPass()
//   awaiting_id        → render IdUpload step
//   awaiting_selfie    → render SelfieCapture step
//   awaiting_verify    → "Verify identity" button → POST /api/identity/verify
//   passed             → call onPass(), modal disappears
//   manual_review      → show "we'll review this" message + continue button
//                        (parent decides whether to let them through)
//   failed             → blocking message; candidate cannot proceed
//   error              → network failure; offer retry
//
// Props:
//   testId             — required, links the verification to this test
//   applicationId?     — optional, surfaces to recruiter dashboard
//   candidateEmail?    — optional, recorded with the verification
//   onPass()           — called once the gate is cleared
//   allowManualReviewContinue?: boolean
//     — default false. When true, "manual_review" verdict also calls
//       onPass() so the candidate continues but the recruiter sees the
//       flag in the dashboard. Best for high-volume hiring where blocking
//       all borderline candidates is worse than letting them through with
//       a recruiter note.

import React, { useEffect } from 'react';
import {
  Shield, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw,
} from 'lucide-react';
import { useIdentityVerification } from './useIdentityVerification';
import IdUpload from './IdUpload';
import SelfieCapture from './SelfieCapture';

export default function IdentityGate({
  testId,
  applicationId = null,
  candidateEmail = null,
  onPass,
  allowManualReviewContinue = false,
}) {
  const {
    config, row, status, error, busy,
    uploadId, uploadSelfie, verify, retry, refresh,
  } = useIdentityVerification(testId, { applicationId, candidateEmail });

  // ── Auto-resolve when the gate has nothing to enforce ──────────────────
  // (no identity sub-features enabled OR already passed)
  useEffect(() => {
    if (status === 'skipped' || status === 'passed') {
      onPass?.();
    }
    if (status === 'manual_review' && allowManualReviewContinue) {
      onPass?.();
    }
  }, [status, allowManualReviewContinue, onPass]);

  // Gate doesn't block when no enforcement is needed.
  if (status === 'skipped' || status === 'passed') return null;
  if (status === 'manual_review' && allowManualReviewContinue) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="flex items-center gap-2 px-6 py-4 border-b border-gray-200">
          <Shield className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Verify your identity
          </h2>
        </header>

        <div className="px-6 py-5 space-y-5">
          <ProgressBar status={status} />

          <Body
            status={status}
            error={error}
            busy={busy}
            row={row}
            config={config}
            uploadId={uploadId}
            uploadSelfie={uploadSelfie}
            verify={verify}
            retry={retry}
            refresh={refresh}
            allowManualReviewContinue={allowManualReviewContinue}
            onPass={onPass}
          />
        </div>

        <footer className="px-6 py-3 border-t border-gray-200 text-xs text-gray-500">
          Your ID and selfie are used only to verify identity for this test.
          They are retained for 90 days and reviewed only if the test is flagged.
        </footer>
      </div>
    </div>
  );
}


// ─── Body — branches on status ────────────────────────────────────────────
function Body({
  status, error, busy, row, config,
  uploadId, uploadSelfie, verify, retry, refresh,
  allowManualReviewContinue, onPass,
}) {
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-10 text-gray-600">
        <Loader2 className="w-6 h-6 mr-2 animate-spin" />
        Loading…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <div className="text-sm text-red-900">
            <div className="font-medium">Couldn't load verification status</div>
            <div className="mt-1">{error || 'Unknown error'}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (status === 'awaiting_id') {
    return <IdUpload onUpload={uploadId} busy={busy} />;
  }

  if (status === 'awaiting_selfie') {
    return <SelfieCapture onUpload={uploadSelfie} busy={busy} />;
  }

  if (status === 'awaiting_verify') {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Verify your identity</h3>
        <p className="text-sm text-gray-600">
          We're ready to compare your selfie with your ID photo. This takes
          a few seconds. The result is shown immediately.
        </p>
        <button
          type="button"
          onClick={verify}
          disabled={busy}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Verify identity
            </>
          )}
        </button>
        {error && (
          <div className="text-sm text-red-700">{error}</div>
        )}
      </div>
    );
  }

  if (status === 'manual_review') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900">
              Verification needs manual review
            </div>
            <div className="text-amber-800 mt-1">
              {row?.verdict_reason || (
                'Your selfie and ID don\'t match closely enough for an ' +
                'automatic pass. A reviewer will check this within 48 hours.'
              )}
            </div>
            {row?.match_similarity !== null && row?.match_similarity !== undefined && (
              <div className="text-amber-700 mt-1 text-xs">
                Match similarity: {Number(row.match_similarity).toFixed(1)}%
              </div>
            )}
          </div>
        </div>

        {allowManualReviewContinue ? (
          <button
            type="button"
            onClick={() => onPass?.()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
          >
            Continue to test
          </button>
        ) : (
          <div className="text-sm text-gray-600">
            Please contact your recruiter or wait for the review to complete.
            You may close this window.
          </div>
        )}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-red-900">
              Identity verification failed
            </div>
            <div className="text-red-800 mt-1">
              {row?.verdict_reason ||
                'We couldn\'t verify your identity. The selfie did not match the ID photo.'}
            </div>
            {row?.match_similarity !== null && row?.match_similarity !== undefined && (
              <div className="text-red-700 mt-1 text-xs">
                Match similarity: {Number(row.match_similarity).toFixed(1)}%
              </div>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Common causes: poor lighting, glasses or mask covering your face,
          or a different angle from your ID photo. Try a fresh selfie below.
          If you believe this is a mistake, please contact your recruiter.
        </div>
        <button
          type="button"
          onClick={retry}
          disabled={busy}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Resetting…
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retake selfie and try again
            </>
          )}
        </button>
        {error && (
          <div className="text-sm text-red-700">{error}</div>
        )}
      </div>
    );
  }

  // Fallback — shouldn't normally render.
  return (
    <div className="text-sm text-gray-600">
      Verification status: {status}
    </div>
  );
}


// ─── ProgressBar — three pips reflecting the wizard step ─────────────────
function ProgressBar({ status }) {
  // Map server-status → visual step index 0/1/2 (no-op for end states).
  const stepIndex =
    status === 'awaiting_id' ? 0 :
    status === 'awaiting_selfie' ? 1 :
    status === 'awaiting_verify' ? 2 :
    -1;

  if (stepIndex < 0) return null;

  const labels = ['Upload ID', 'Take selfie', 'Verify'];
  return (
    <div className="flex items-center gap-2 text-xs">
      {labels.map((label, i) => (
        <React.Fragment key={label}>
          <div className={`flex items-center gap-1.5 ${
            i === stepIndex ? 'font-semibold text-blue-700' :
            i < stepIndex ? 'text-green-700' : 'text-gray-400'
          }`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
              i === stepIndex ? 'bg-blue-600 text-white' :
              i < stepIndex ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {i < stepIndex ? '✓' : i + 1}
            </span>
            {label}
          </div>
          {i < labels.length - 1 && (
            <div className={`h-px flex-1 ${
              i < stepIndex ? 'bg-green-300' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
